// Analytics events are handled at the component level

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  errorCode?: string;
}

export interface LoginState {
  identifier: string;
  identifierError?: string;
  isIdentifierLocked: boolean;
  failCount: number;
  emailErrorCount: number;
  usernameErrorCount: number;
  loginStarted: boolean;
}

/**
 * Email validation regex - similar to Auth0's validation
 * Validates basic email format
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Username validation regex - allows alphanumeric, dots, hyphens, underscores
 * Must be 3-30 characters long
 */
const USERNAME_REGEX = /^[a-zA-Z0-9._-]{3,30}$/;

/**
 * Validates email format
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email) {
    return {
      isValid: false,
      error: 'Enter your email address',
      errorCode: 'email_required',
    };
  }

  if (!EMAIL_REGEX.test(email)) {
    return {
      isValid: false,
      error: 'Enter an email address, like name@example.com',
      errorCode: 'invalid_email',
    };
  }

  return { isValid: true };
};

/**
 * Validates username format
 */
export const validateUsername = (username: string): ValidationResult => {
  if (!username) {
    return {
      isValid: false,
      error: 'Enter a username',
      errorCode: 'invalid_username',
    };
  }

  if (username.length < 3) {
    return {
      isValid: false,
      error: "Enter a username that's 3 characters or more",
      errorCode: 'invalid_username',
    };
  }

  if (username.length > 30) {
    return {
      isValid: false,
      error: "Enter a username that's 30 characters or fewer",
      errorCode: 'invalid_username',
    };
  }

  if (!USERNAME_REGEX.test(username)) {
    return {
      isValid: false,
      error:
        'Usernames can only include letters, numbers, underscores ( _ ), periods ( . ) and hyphens ( - )',
      errorCode: 'invalid_username',
    };
  }

  return { isValid: true };
};

/**
 * Determines if input looks like an email or username and validates accordingly
 */
export const validateEmailOrUsername = (input: string): ValidationResult => {
  if (!input) {
    return {
      isValid: false,
      error: 'Enter your email or username',
      errorCode: 'identifier_required',
    };
  }

  const trimmedInput = input.trim();

  // If it contains @ symbol, treat as email
  if (trimmedInput.includes('@')) {
    return validateEmail(trimmedInput);
  }

  // Otherwise treat as username
  return validateUsername(trimmedInput);
};
